import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Package, CheckCircle2, DollarSign, Clock, Wrench, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { bcStackGenerate } from "@/lib/api";

export default function BusinessCenterStackBuilder() {
  const [params] = useSearchParams();
  const aid = params.get("assessment_id");
  const [stack, setStack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [prefer, setPrefer] = useState("either");
  const [budget, setBudget] = useState("");

  useEffect(() => { document.title = "AI Stack Builder | WebsiteTech.pro"; }, []);

  const generate = async () => {
    if (!aid) return setErr("You need to complete the assessment first.");
    setBusy(true); setErr(null);
    try {
      const s = await bcStackGenerate({ assessment_id: aid, prefer, budget_monthly_usd: budget ? Number(budget) : null });
      setStack(s);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not build your stack");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="bc-stack">
        <div className="max-w-5xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/business-center" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Business Center</Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">AI Stack Builder</h1>
          <p className="mt-2 text-slate-600 max-w-2xl">Turns your assessment into a step-by-step recommended AI stack — with buy-vs-build calls, setup effort, dependencies and a monthly cost band. Editable.</p>

          {!aid && (
            <div className="mt-6 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm p-4">
              Start with the <Link to="/business-center/assessment" className="font-semibold underline">AI Readiness Assessment</Link> first — the builder uses your scores to prioritize your stack.
            </div>
          )}

          {aid && !stack && (
            <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6">
              <div className="text-sm text-slate-500 uppercase tracking-widest font-bold">Preferences</div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Preference</div>
                  <select data-testid="bc-stack-prefer" value={prefer} onChange={(e) => setPrefer(e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                    <option value="either">Either existing tools or WebsiteTech-built</option>
                    <option value="websitetech">Prefer WebsiteTech-built</option>
                    <option value="existing">Prefer existing tools</option>
                  </select>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Monthly budget (USD, optional)</div>
                  <input data-testid="bc-stack-budget" type="number" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="e.g. 500" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              {err && <div className="mt-3 text-xs text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
              <button data-testid="bc-stack-generate" onClick={generate} disabled={busy} className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-3 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />} Build my stack <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {stack && <StackView stack={stack} />}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function StackView({ stack }) {
  return (
    <div className="mt-8 space-y-8" data-testid="bc-stack-view">
      {/* Cost summary */}
      <div className="rounded-3xl overflow-hidden shadow-xl bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white p-8">
        <div className="text-xs uppercase tracking-widest font-bold opacity-80 flex items-center gap-2"><DollarSign className="w-4 h-4" /> Estimated cost band</div>
        <div className="mt-2 grid grid-cols-2 gap-4">
          <div>
            <div className="text-4xl md:text-5xl font-black tabular-nums">${stack.cost_range.monthly_low}<span className="text-lg opacity-80"> – ${stack.cost_range.monthly_high}</span></div>
            <div className="text-sm opacity-80 mt-1">per month</div>
          </div>
          <div>
            <div className="text-4xl md:text-5xl font-black tabular-nums">${stack.cost_range.annual_low.toLocaleString()}<span className="text-lg opacity-80"> – ${stack.cost_range.annual_high.toLocaleString()}</span></div>
            <div className="text-sm opacity-80 mt-1">per year (incl. one-time)</div>
          </div>
        </div>
        <div className="mt-4 text-xs opacity-70">Ranges reflect vendor pricing bands as of last verification. Confirm exact fees during scoping.</div>
      </div>

      {/* Core stack */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Your recommended stack</h2>
        <div className="mt-4 space-y-3">
          {stack.core_stack.map((m, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start gap-4" data-testid={`bc-stack-item-${i}`}>
              <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 grid place-items-center font-bold flex-shrink-0">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-2">
                  <div className="font-semibold text-slate-900">{m.module}</div>
                  <span className="text-[10px] uppercase tracking-widest font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5">
                    {m.buy_or_build === "build_websitetech" ? "Build w/ WebsiteTech" : "Use existing"}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest font-bold bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-2 py-0.5">{m.category}</span>
                </div>
                <div className="mt-1 text-sm text-slate-700">{m.why || m.why_build}</div>
                <div className="mt-2 text-xs text-slate-500 flex flex-wrap gap-4">
                  <span><Clock className="w-3 h-3 inline mr-1" /> {m.setup_days}-day setup ({m.setup_effort})</span>
                  <span><DollarSign className="w-3 h-3 inline mr-1" /> {m.recurring_band}</span>
                  {(m.dependencies || []).length > 0 && <span><Wrench className="w-3 h-3 inline mr-1" /> Needs: {m.dependencies.join(", ")}</span>}
                </div>
              </div>
              <Link to={m.route} className="text-xs font-semibold text-indigo-700 hover:text-indigo-900">Details →</Link>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly rollout */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Rollout sequence</h2>
        <p className="mt-1 text-sm text-slate-600">Ship 1–2 modules per week to keep team focus and let each piece prove value.</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {stack.implementation_sequence.map((w, i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-4" data-testid={`bc-stack-wave-${i}`}>
              <div className="text-xs uppercase tracking-widest font-bold text-indigo-700">Week {w.week}</div>
              <ul className="mt-2 space-y-1.5">
                {w.modules.map((m, j) => (
                  <li key={j} className="text-sm text-slate-800 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> {m.module}
                    <span className="text-xs text-slate-500 ml-auto">{m.setup_days}d</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {stack.extended_stack.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Extended (over budget)</h2>
          <p className="mt-1 text-sm text-slate-600">These fell outside your budget cap — add them in a later phase.</p>
          <ul className="mt-3 space-y-1 text-sm text-slate-700">
            {stack.extended_stack.map((m, i) => <li key={i} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2"><span className="font-semibold">{m.module}</span><span className="text-xs text-slate-500 ml-auto">{m.recurring_band}</span></li>)}
          </ul>
        </div>
      )}

      <div className="rounded-3xl bg-slate-950 text-white p-8">
        <h2 className="text-xl md:text-2xl font-bold">Want us to implement this?</h2>
        <p className="mt-2 text-slate-300 text-sm">We'll turn this stack into a proposal — with exact modules, timeline and cost. Your assessment + stack are attached.</p>
        <Link to="/contact" data-testid="bc-stack-request" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 px-5 py-3 font-semibold transition">Request implementation <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );
}
